import { z } from 'zod'
import {
  BeaconEffortSchema,
  BeaconPhaseSchema,
  BeaconStageSchema,
  CoverageVerdictSchema,
  ExploitChainsPhaseSchema,
  ExploitChainsStatusSchema,
  FindingOverlayPhaseSchema,
  FindingOverlayStatusSchema,
  LensBudgetSchema,
  OpenGrepRulesPhaseSchema,
  OpenGrepRulesStatusSchema,
  ThreatModelPhaseSchema,
  ThreatModelStatusSchema,
} from './api-schemas.ts'

/**
 * Zod schemas for Server-Sent Event (SSE) analysis stream events.
 *
 * These events stream real-time analysis progress for an individual result via
 * `GET /api/v1/{triage,fix,sca}-results/{result_id}/activity` (see `ACTIVITY_ENDPOINT_BY_TYPE`).
 * Event format: `data: {json}\n\n`
 */

// Start event - signals workflow has begun
export const StartEventSchema = z
  .object({
    type: z.literal('start'),
    id: z.string(),
    timestamp: z.string(),
  })
  .describe('StartEvent')

// The analysis result types the platform routes on. Declared once here because two events in this file
// carry it; `api-schemas.ts`, `api-hooks.ts` and `use-activity-result-selection.ts` still hand-list the
// same vocabulary, which is existing code left alone here.
const FindingTypeSchema = z.enum(['triage', 'fix', 'sca'])

// The identity every activity event carries, widget or not — a widget is an activity whose payload
// redraws, so both event shapes below spread these rather than declaring them twice.
//
// `activity_id` is the fold and idempotency key, stable across every emission for one activity;
// `activity_sequence` is the freshness key, counted per `activity_id`, so the client folds to
// `max(activity_sequence)` and a stale or repeated re-send is a no-op; `container_id` is the `activity_id`
// of the containing activity, omitted entirely (not null) for a top-level activity.
//
// All four are optional as consumer-side defense, and `timestamp` needs the strongest case because the
// producer has stamped it on every event since analysis-service `16c350544` (2026-05-04). The platform
// persists each raw frame verbatim and replays the whole log from offset 0 on every connection, and
// `analysis_result_event` has held rows since 2026-03-20 with no retention job to age them out — so
// results analyzed in that six-week window carry frames with no `timestamp`. Requiring it would reject
// every one of those at `schema.parse` and render an empty feed where the steps render today, in
// exchange for a field nothing here reads. Tightening belongs with the CI contract check planned in
// ISS-8778, which fails there instead of degrading silently here.
const ACTIVITY_IDENTITY_FIELDS = {
  activity_id: z.string().optional(),
  activity_sequence: z.number().int().nonnegative().optional(),
  container_id: z.string().optional(),
  timestamp: z.string().optional(),
}

// Finding progress content - the data inside data-progress-finding events
export const FindingProgressContentSchema = z
  .object({
    finding_id: z.string(),
    type: FindingTypeSchema,
    status: z.enum(['in_progress', 'completed', 'error']),
    title: z.string(),
    description: z.string(),
    completed_events: z.number().optional(),
    total_events: z.number().optional(),
    ...ACTIVITY_IDENTITY_FIELDS,
  })
  .describe('FindingProgressContent')

// Data progress finding event - individual finding analysis updates
export const DataProgressFindingEventSchema = z
  .object({
    type: z.literal('data-progress-finding'),
    content: FindingProgressContentSchema,
  })
  .describe('DataProgressFindingEvent')

// Widget payload - an open vocabulary today. `kind` names the visual treatment and `message` is the
// payload's plain-text summary, which every kind carries so an unrecognized one still renders something
// rather than a blank card.
//
// Not a discriminated union while one kind exists and nothing narrows on it. ISS-8082 adds the
// dependency-graph kind, which is when narrowing becomes real.
//
// `message` is agent-derived text — upstream it is a join of tool-call summaries — and must be rendered
// as text, never as markup. The schema does not enforce that; the renderer does.
export const WidgetPayloadSchema = z
  .object({
    kind: z.string(),
    message: z.string().optional(),
  })
  .describe('WidgetPayload')

// Widget snapshot content - the complete current state of one widget, never a delta. The platform never
// de-duplicates and replays the whole event log from offset 0, so only a full snapshot is idempotent and
// order-tolerant. See `ACTIVITY_IDENTITY_FIELDS` for the identity this shares with progress events; the
// fold drops a widget missing `activity_id` rather than inventing one, since it has no stable fold key.
export const WidgetSnapshotContentSchema = z
  .object({
    finding_id: z.string(),
    type: FindingTypeSchema,
    payload: WidgetPayloadSchema,
    ...ACTIVITY_IDENTITY_FIELDS,
  })
  .describe('WidgetSnapshotContent')

// Data widget snapshot event - redraws one widget in place inside its container activity
export const DataWidgetSnapshotEventSchema = z
  .object({
    type: z.literal('data-widget-snapshot'),
    content: WidgetSnapshotContentSchema,
  })
  .describe('DataWidgetSnapshotEvent')

// Workflow event - overall workflow status updates
export const WorkflowEventSchema = z
  .object({
    type: z.literal('workflow'),
    content: z.object({
      status: z.enum(['in_progress', 'completed', 'error']),
      title: z.string(),
      description: z.string(),
      error: z.string().optional(),
    }),
  })
  .describe('WorkflowEvent')

// Triage completion content - signals triage process is complete for a finding
export const TriageCompleteContentSchema = z
  .object({
    finding_id: z.string(),
    type: z.literal('triage'),
    resultType: z.enum(['TRIAGED', 'RULE_NOT_SUPPORTED', 'ERROR']),
    triageStatus: z.enum(['TRUE_POSITIVE', 'FALSE_POSITIVE', 'WONT_FIX', 'SUSPICIOUS']).nullable(),
  })
  .describe('TriageCompleteContent')

// Fix completion content - signals fix process is complete for a finding
export const FixCompleteContentSchema = z
  .object({
    finding_id: z.string(),
    type: z.literal('fix'),
    outcome: z.enum(['fixed', 'blocked', 'excluded', 'out_of_scope']),
    reason: z.string().nullable().optional(),
  })
  .describe('FixCompleteContent')

// SCA completion content - signals SCA analysis is complete for a finding
export const ScaCompleteContentSchema = z
  .object({
    finding_id: z.string(),
    type: z.literal('sca'),
    outcome: z.enum(['completed', 'excluded']),
    reason: z.string().nullable().optional(),
  })
  .describe('ScaCompleteContent')

// Discriminated union for finding completion events
export const FindingCompleteContentSchema = z
  .discriminatedUnion('type', [TriageCompleteContentSchema, FixCompleteContentSchema, ScaCompleteContentSchema])
  .describe('FindingCompleteContent')

// Data progress finding complete event - signals triage or fix process is complete
export const DataProgressFindingCompleteEventSchema = z
  .object({
    type: z.literal('data-progress-finding-complete'),
    content: FindingCompleteContentSchema,
  })
  .describe('DataProgressFindingCompleteEvent')

// Finish event - signals workflow has completed
export const FinishEventSchema = z
  .object({
    type: z.literal('finish'),
    id: z.string(),
    timestamp: z.string(),
    usage: z.record(z.string(), z.unknown()).optional(),
  })
  .describe('FinishEvent')

// Error event - signals an error occurred
export const ErrorEventSchema = z
  .object({
    type: z.literal('error'),
    content: z.string(),
  })
  .describe('ErrorEvent')

// Discriminated union of all SSE event types
export const AnalysisStreamEventSchema = z
  .discriminatedUnion('type', [
    StartEventSchema,
    DataProgressFindingEventSchema,
    DataProgressFindingCompleteEventSchema,
    DataWidgetSnapshotEventSchema,
    WorkflowEventSchema,
    FinishEventSchema,
    ErrorEventSchema,
  ])
  .describe('AnalysisStreamEvent')

// Support bundle phase event - emitted on every phase transition by the platform (ISS-7005)
export const SupportBundlePhaseEventSchema = z
  .object({
    id: z.string(),
    phase: z.enum(['pending', 'running', 'succeeded', 'failed']),
    elapsed_seconds: z.number().int().nonnegative(),
  })
  .describe('SupportBundlePhaseEvent')

// Progress event for a repository-scoped generation stream (threat model, exploit chains). The SSE
// `event:` name is the phase for progress events (status `processing`) or the terminal status for
// the terminal event; a terminal `failed` event additionally carries `message` = the failure reason.
const createProgressStreamEventSchema = <TPhase extends z.ZodType, TStatus extends z.ZodType>(
  phaseSchema: TPhase,
  statusSchema: TStatus,
  name: string
) =>
  z
    .object({
      phase: phaseSchema,
      status: statusSchema,
      timestamp: z.string(),
      message: z.string().optional(),
    })
    .describe(name)

export const ThreatModelStreamEventSchema = createProgressStreamEventSchema(
  ThreatModelPhaseSchema,
  ThreatModelStatusSchema,
  'ThreatModelStreamEvent'
)

// #region Exploit chains correlation progress

// The correlation pass narrates itself with named events inside the lifecycle phases. Their
// `data:` payload repeats the SSE event name as `event` and carries a `detail` object shaped per
// name. These are analysis-service's own wire shapes, proxied untouched by the platform, so they
// are snake_case unlike the platform's camelCase responses.
export const ExploitChainsProgressEventNameSchema = z
  .enum([
    'inputs_loaded',
    'index_built',
    'lens_started',
    'lens_proposed',
    'lens_failed',
    'candidates_merged',
    'candidate_verifying',
    'candidate_verdict',
    'chains_grounded',
  ])
  .describe('ExploitChainsProgressEventName')
export type ExploitChainsProgressEventName = z.infer<typeof ExploitChainsProgressEventNameSchema>

export const ExploitChainsStreamEventSchema = createProgressStreamEventSchema(
  ExploitChainsPhaseSchema,
  ExploitChainsStatusSchema,
  'ExploitChainsStreamEvent'
).extend({
  event: ExploitChainsProgressEventNameSchema.optional(),
  detail: z.unknown().optional(),
})

export const ProgressToolCountSchema = z.object({
  tool: z.string(),
  kind: z.string(),
  count: z.number().int().nonnegative(),
  finding_ids: z.string().array().default([]),
})
export type ProgressToolCount = z.infer<typeof ProgressToolCountSchema>

export const InputsLoadedDetailSchema = z.object({
  findings: z.object({
    total: z.number().int().nonnegative(),
    by_tool: ProgressToolCountSchema.array(),
  }),
  threat_model_generated_at: z.string().nullish(),
})

export const IndexBuiltDetailSchema = z.object({
  indexed: z.number().int().nonnegative(),
  dropped_ids: z.string().array().default([]),
  tokens_used: z.number().int().nonnegative(),
  tokens_allowed: z.number().int().nonnegative(),
})

export const LensStartedDetailSchema = z.object({ lens: z.string() })
export const LensProposedDetailSchema = z.object({ lens: z.string(), proposed: z.number().int().nonnegative() })
export const LensFailedDetailSchema = z.object({ lens: z.string(), error: z.string() })

export const CandidateFindingRefSchema = z.object({
  finding_id: z.string(),
  rule: z.string().default(''),
  path: z.string().default(''),
  title: z.string().nullish(),
  tool: z.string().nullish(),
})
export type CandidateFindingRef = z.infer<typeof CandidateFindingRefSchema>

export const CandidateProposalSchema = z.object({
  id: z.string(),
  lenses: z.string().array().default([]),
  finding_ids: z.string().array().default([]),
  findings: CandidateFindingRefSchema.array().default([]),
  hypothesis: z.string().default(''),
})
export type CandidateProposal = z.infer<typeof CandidateProposalSchema>

export const CandidatesMergedDetailSchema = z.object({
  raw: z.number().int().nonnegative(),
  unique: z.number().int().nonnegative(),
  merged: z.number().int().nonnegative(),
  candidates: CandidateProposalSchema.array().default([]),
})

export const CandidateVerifyingDetailSchema = z.object({ candidate_id: z.string() })

export const VerdictSchema = z.enum(['confirmed', 'rejected', 'errored']).describe('Verdict')
export type Verdict = z.infer<typeof VerdictSchema>

export const CandidateVerdictDetailSchema = z.object({
  candidate_id: z.string(),
  verdict: VerdictSchema,
  reason: z.string().default(''),
  title: z.string().nullish(),
  severity: z.string().nullish(),
})

export const ChainsGroundedDetailSchema = z.object({
  chains_in: z.number().int().nonnegative(),
  chains_out: z.number().int().nonnegative(),
  references_repaired: z.number().int().nonnegative(),
  references_dropped: z.number().int().nonnegative(),
})

// #endregion

export const FindingOverlaysStreamEventSchema = createProgressStreamEventSchema(
  FindingOverlayPhaseSchema,
  FindingOverlayStatusSchema,
  'FindingOverlaysStreamEvent'
)

export const OpenGrepRulesStreamEventSchema = createProgressStreamEventSchema(
  OpenGrepRulesPhaseSchema,
  OpenGrepRulesStatusSchema,
  'OpenGrepRulesStreamEvent'
)

// #region Pixee Beacon run stream: `GET /api/v1/ai-scans/{run_id}/events`

/**
 * Every frame of a Beacon run, before its `detail` is checked. The stream hook parses frames with
 * this lenient envelope so one malformed detail costs that one event, not a console error per frame
 * or the stream; {@link parseBeaconEvent} then checks the detail against its event's shape.
 */
export const BeaconStreamFrameSchema = z
  .object({
    seq: z.number().int(),
    ts: z.string(),
    status: z.string(),
    name: z.string(),
    detail: z.record(z.string(), z.unknown()),
    message: z.string(),
  })
  .describe('BeaconStreamFrame')
export type BeaconStreamFrame = z.infer<typeof BeaconStreamFrameSchema>

const EntryPointRefSchema = z.object({ id: z.string(), name: z.string(), kind: z.string(), location: z.string() })
const AssetRefSchema = z.object({ id: z.string(), name: z.string(), kind: z.string() })
const ControlRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.string(),
  auditable: z.boolean(),
  applies_to: z.array(z.string()),
})
const OperationRefSchema = z.object({
  id: z.string(),
  group: z.string(),
  trigger: z.string(),
  handler: z.string(),
  location: z.string(),
  caller: z.string(),
  effect: z.string(),
  asset_ids: z.array(z.string()),
})
const FocusTargetsSchema = z.object({
  entry_point_ids: z.array(z.string()),
  operation_ids: z.array(z.string()),
  control_ids: z.array(z.string()),
  asset_ids: z.array(z.string()),
})
const CandidateRefSchema = z.object({
  finding_uuid: z.string(),
  title: z.string(),
  weakness: z.string(),
  cwe_ids: z.array(z.string()),
  file_path: z.string(),
  line: z.number().int(),
  confidence: z.number(),
})
const InvestigationRefSchema = z.object({
  investigation_id: z.string(),
  lens: z.string(),
  focus_kind: z.string(),
  label: z.string(),
  targets: FocusTargetsSchema,
})

export type BeaconEntryPointRef = z.infer<typeof EntryPointRefSchema>
export type BeaconAssetRef = z.infer<typeof AssetRefSchema>
export type BeaconControlRef = z.infer<typeof ControlRefSchema>
export type BeaconOperationRef = z.infer<typeof OperationRefSchema>
export type BeaconCandidateRef = z.infer<typeof CandidateRefSchema>
export type BeaconInvestigationRef = z.infer<typeof InvestigationRefSchema>

const beaconEvent = <TName extends string, TDetail extends z.ZodType>(name: TName, detail: TDetail) =>
  z.object({
    seq: z.number().int(),
    ts: z.string(),
    status: z.string(),
    name: z.literal(name),
    detail,
    message: z.string(),
  })

const lifecycleEvent = <TName extends z.infer<typeof BeaconPhaseSchema>>(name: TName) =>
  beaconEvent(name, z.record(z.string(), z.unknown()))

export const BeaconEventSchema = z
  .discriminatedUnion('name', [
    lifecycleEvent('queued'),
    lifecycleEvent('fetching'),
    lifecycleEvent('generating'),
    lifecycleEvent('triaging'),
    lifecycleEvent('completed'),
    lifecycleEvent('failed'),
    lifecycleEvent('cancelled'),
    beaconEvent(
      'scan_configured',
      z.object({ effort: BeaconEffortSchema, token_budget: z.number(), mode: z.string() })
    ),
    beaconEvent('stage_started', z.object({ stage: BeaconStageSchema })),
    beaconEvent('stage_finished', z.object({ stage: BeaconStageSchema })),
    beaconEvent(
      'threat_model_loaded',
      z.object({
        entry_points: z.array(EntryPointRefSchema),
        assets: z.array(AssetRefSchema),
        controls: z.array(ControlRefSchema),
      })
    ),
    beaconEvent(
      'routes_reconciled',
      z.object({
        routes_outside_threat_model: z.number(),
        routes_added_as_entry_points: z.number(),
        // Entry points for routes the threat model missed; their mapping events follow.
        entry_points: z.array(EntryPointRefSchema).optional(),
      })
    ),
    beaconEvent('entry_point_mapping_started', z.object({ entry_point_id: z.string(), name: z.string().optional() })),
    beaconEvent(
      'entry_point_mapped',
      z.object({
        entry_point_id: z.string(),
        name: z.string().optional(),
        outcome: z.enum(['mapped', 'empty', 'incomplete']),
        reason: z.string(),
        operations: z.array(OperationRefSchema),
        files_read: z.number().nullable(),
        tokens_spent: z.number(),
      })
    ),
    beaconEvent(
      'sinks_inventoried',
      z.object({ count: z.number(), error: z.string().nullable(), beyond_cap: z.number() })
    ),
    beaconEvent(
      'plan_built',
      z.object({
        lenses_total: z.number().int(),
        lenses: z.array(z.string()),
        investigations: z.array(InvestigationRefSchema),
      })
    ),
    beaconEvent('investigation_started', InvestigationRefSchema),
    beaconEvent(
      'investigation_finished',
      InvestigationRefSchema.extend({
        candidates: z.array(CandidateRefSchema),
        // `skipped` (the budget ran out) arrives with no started event before it.
        outcome: z.enum(['completed', 'incomplete', 'skipped']).optional(),
        reason: z.string().optional(),
        files_read: z.number(),
        tokens_spent: z.number(),
        duration_seconds: z.number(),
      })
    ),
    beaconEvent('lens_finished', LensBudgetSchema),
    beaconEvent(
      'budget_snapshot',
      z.object({ token_budget: z.number(), spent: z.number(), by_lens: z.record(z.string(), z.number()) })
    ),
    beaconEvent('candidates_deduped', z.object({ raw: z.number(), deduped: z.number() })),
    beaconEvent(
      'candidate_verified',
      z.object({
        finding_uuid: z.string(),
        verdict: z.enum(['upheld', 'refuted']),
        refuted_on: z.array(z.string()),
        title: z.string().optional(),
      })
    ),
    beaconEvent(
      'candidate_triaged',
      z.object({
        finding_uuid: z.string(),
        result_type: z.string(),
        kept: z.boolean(),
        title: z.string(),
        lens: z.string(),
      })
    ),
    beaconEvent(
      'findings_progress',
      z.object({
        processed: z.number(),
        total: z.number(),
        findings_count: z.number().optional(),
        // While investigating the counts are investigations; while verifying, candidates.
        stage: BeaconStageSchema.optional(),
      })
    ),
    beaconEvent('summary_ready', z.object({ examined_percent: z.number(), verdict: CoverageVerdictSchema })),
    beaconEvent('cancel_requested', z.record(z.string(), z.unknown())),
  ])
  .describe('BeaconEvent')
export type BeaconEvent = z.infer<typeof BeaconEventSchema>
export type BeaconEventName = BeaconEvent['name']

/** Every event name the stream carries, for the SSE hook's allowlist; anything else (keep-alives) is dropped. */
export const BEACON_EVENT_NAMES: readonly BeaconEventName[] = BeaconEventSchema.options.map(
  option => option.shape.name.value
)

/** The frame as a typed event, or `undefined` when its name is unknown or its detail malformed. */
export const parseBeaconEvent = (frame: BeaconStreamFrame): BeaconEvent | undefined =>
  BeaconEventSchema.safeParse(frame).data

/** A run's stream ends with one of these; nothing follows it. */
export const isTerminalBeaconFrame = (frame: Pick<BeaconStreamFrame, 'name'>): boolean =>
  frame.name === 'completed' || frame.name === 'failed' || frame.name === 'cancelled'

// #endregion

// #region Findings Intake session stream: `GET /api/v1/organizations/{org_id}/freeform-uploads/{session_id}/events`

// Hand-maintained mirror of the freeform upload-session SSE union defined in the v1 integration
// contract §B8 (pixee-platform). The events endpoint has no OpenAPI payload schema, so — like the
// coding-guidelines document events — these are hand-written zod discriminated on `type`.
export const FreeformStatusPhaseSchema = z.enum(['parsing', 'associating', 'scanning', 'completed', 'failed'])
export type FreeformStatusPhase = z.infer<typeof FreeformStatusPhaseSchema>

const FreeformStatusEventSchema = z.object({
  type: z.literal('status'),
  phase: FreeformStatusPhaseSchema,
  detail: z.string().optional(),
})

const FreeformFileEventSchema = z.object({
  type: z.literal('file'),
  filename: z.string(),
  parseStatus: z.string(),
})

// `lowestConfidence`/`weakestRationale` describe the association the agent was least sure of in this
// repository's group. Both are optional: the agent may not emit a confidence, and older sessions
// replayed from before the field existed will not carry one.
const FreeformRepoScanEventSchema = z.object({
  type: z.literal('repo_scan'),
  repoFullName: z.string(),
  scanId: z.string(),
  findingCount: z.number().int(),
  lowestConfidence: z.number().optional(),
  weakestRationale: z.string().optional(),
})

// Each unassociated finding carries the machine-readable outcome (`reason`) and the agent's prose
// (`rationale`) explaining it. `reason` is left as a free string rather than an enum on purpose: the
// wire vocabulary is the union of the analysis-service association taxonomy and the platform's own
// scan-creation outcomes, and either side may add a value — the UI maps known reasons and falls back
// gracefully. `findings` is optional so a platform from before the enriched event (or a session
// replayed from before it) that sends only `count` still parses.
const FreeformUnassociatedItemSchema = z.object({
  title: z.string(),
  reason: z.string(),
  rationale: z.string().optional().default(''),
})
export type FreeformUnassociatedItem = z.infer<typeof FreeformUnassociatedItemSchema>

const FreeformUnassociatedEventSchema = z.object({
  type: z.literal('unassociated'),
  count: z.number().int(),
  findings: z.array(FreeformUnassociatedItemSchema).optional(),
})

const FreeformDoneEventSchema = z.object({
  type: z.literal('done'),
})

const FreeformErrorEventSchema = z.object({
  type: z.literal('error'),
  reason: z.string(),
})

// What analysis-service is looking at as it reads the files and places each finding. The envelope is
// lenient on purpose: `detail` is checked against its event's shape by `parseFreeformProgress`, so one
// malformed or unknown event costs that event, not the stream.
const FreeformProgressFrameSchema = z.object({
  type: z.literal('progress'),
  seq: z.number().int(),
  ts: z.string(),
  name: z.string(),
  detail: z.record(z.string(), z.unknown()),
  message: z.string(),
})
export type FreeformProgressFrame = z.infer<typeof FreeformProgressFrameSchema>

export const FreeformUploadEventSchema = z.discriminatedUnion('type', [
  FreeformStatusEventSchema,
  FreeformProgressFrameSchema,
  FreeformFileEventSchema,
  FreeformRepoScanEventSchema,
  FreeformUnassociatedEventSchema,
  FreeformDoneEventSchema,
  FreeformErrorEventSchema,
])

export type FreeformUploadEvent = z.infer<typeof FreeformUploadEventSchema>

// #endregion

// Type exports
export type StartEvent = z.infer<typeof StartEventSchema>
export type FindingProgressContent = z.infer<typeof FindingProgressContentSchema>
export type DataProgressFindingEvent = z.infer<typeof DataProgressFindingEventSchema>
export type TriageCompleteContent = z.infer<typeof TriageCompleteContentSchema>
export type FixCompleteContent = z.infer<typeof FixCompleteContentSchema>
export type ScaCompleteContent = z.infer<typeof ScaCompleteContentSchema>
export type FindingCompleteContent = z.infer<typeof FindingCompleteContentSchema>
export type DataProgressFindingCompleteEvent = z.infer<typeof DataProgressFindingCompleteEventSchema>
export type WidgetPayload = z.infer<typeof WidgetPayloadSchema>
export type WidgetSnapshotContent = z.infer<typeof WidgetSnapshotContentSchema>
export type DataWidgetSnapshotEvent = z.infer<typeof DataWidgetSnapshotEventSchema>
export type WorkflowEvent = z.infer<typeof WorkflowEventSchema>
export type FinishEvent = z.infer<typeof FinishEventSchema>
export type ErrorEvent = z.infer<typeof ErrorEventSchema>
export type AnalysisStreamEvent = z.infer<typeof AnalysisStreamEventSchema>
export type SupportBundlePhaseEvent = z.infer<typeof SupportBundlePhaseEventSchema>
export type ThreatModelStreamEvent = z.infer<typeof ThreatModelStreamEventSchema>
export type ExploitChainsStreamEvent = z.infer<typeof ExploitChainsStreamEventSchema>
export type FindingOverlaysStreamEvent = z.infer<typeof FindingOverlaysStreamEventSchema>
export type OpenGrepRulesStreamEvent = z.infer<typeof OpenGrepRulesStreamEventSchema>

// Backwards compatibility alias - FindingProgressContent is what consumers need for the event buffer
export type FindingProgressEvent = FindingProgressContent
